if (!customElements.get('header-cart')) {
  customElements.define('header-cart', class HeaderCart extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('#cart-icon-bubble');
      this.panel = this.querySelector('#header-cart-dropdown');
      this.desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };

      this.trigger.addEventListener('click', (event) => {
        // Keep native navigation, including new-tab clicks, on desktop.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (this.desktop.matches && event.pointerType !== 'touch') return;
        if (!this.panel.hidden) return;
        event.preventDefault();
        this.setOpen(true);
      }, options);

      this.addEventListener('pointerenter', (event) => {
        if (this.desktop.matches && event.pointerType === 'mouse') this.setOpen(true);
      }, options);
      this.addEventListener('pointerleave', (event) => {
        if (this.desktop.matches && event.pointerType === 'mouse' && !this.panel.contains(document.activeElement)) {
          this.setOpen(false);
        }
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.setOpen(false);
          this.trigger.focus();
        } else if (event.target === this.trigger && (event.key === 'ArrowDown' || event.key === ' ')) {
          event.preventDefault();
          this.setOpen(true);
          this.panel.querySelector('a').focus();
        }
      }, options);
      this.addEventListener('focusout', (event) => {
        // Tapping static content can blur the icon without focusing another element.
        // Outside taps are handled by pointerdown; only close for a known outside focus target.
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.setOpen(false);
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      }, options);
      this.desktop.addEventListener('change', () => this.setOpen(false), options);

      // Delegate to the persistent panel: Shopify replaces its contents after each change.
      this.panel.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('.quantity__input') || this.updating) return;
        if (!input.checkValidity()) {
          input.reportValidity();
          input.value = input.defaultValue;
          input.closest('quantity-input').validateQtyRules();
          return;
        }
        if (input.value === input.defaultValue) return;
        this.updateQuantity(input.closest('.header-cart__item'), input.valueAsNumber);
      }, options);
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('.quantity__input')) {
          event.preventDefault();
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, options);
      this.panel.addEventListener('click', (event) => {
        const remove = event.target.closest('.header-cart__remove');
        if (!remove || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!this.updating) this.updateQuantity(remove.closest('.header-cart__item'), 0);
      }, options);
    }

    disconnectedCallback() {
      this.setOpen(false);
      this.controller.abort();
    }

    getSectionsToRender() {
      const sections = [
        { id: 'header-cart-dropdown', section: 'header-cart-dropdown', selector: '.shopify-section' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
      ['main-cart-items', 'main-cart-footer'].forEach((id) => {
        const element = document.getElementById(id);
        if (element) sections.push({ id, section: element.dataset.id, selector: '.js-contents' });
      });
      if (document.getElementById('CartDrawer')) {
        sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
      }
      return sections;
    }

    async updateQuantity(item, quantity) {
      if (this.updating || !item) return;
      const key = item.dataset.itemKey;
      const index = Array.from(this.panel.querySelectorAll('.header-cart__item')).indexOf(item);
      const activeElement = document.activeElement;
      const focusName = activeElement?.getAttribute('name');
      const listScroll = this.panel.querySelector('.header-cart__items').scrollTop;
      const sections = this.getSectionsToRender();
      let failureMessage = window.cartStrings.error;
      this.showError('');
      this.setBusy(true);

      try {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          signal: this.controller.signal,
          body: JSON.stringify({
            id: key,
            quantity,
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname
          })
        });
        const state = await response.json();
        if (!response.ok || state.errors) {
          failureMessage = typeof state.errors === 'string' ? state.errors : state.description || window.cartStrings.error;
          throw new Error(failureMessage);
        }

        // A cart change can succeed while Shopify fails to render a bundled section.
        // Retry only the section read; never repeat the cart mutation.
        if (sections.some((section) => !state.sections?.[section.section])) {
          const url = new URL(routes.cart_url, window.location.origin);
          url.searchParams.set('sections', sections.map((section) => section.section).join(','));
          const sectionResponse = await fetch(url, { signal: this.controller.signal });
          if (!sectionResponse.ok) throw new Error(window.cartStrings.error);
          state.sections = await sectionResponse.json();
        }

        const replacements = sections.map((section) => {
          const element = document.getElementById(section.id);
          const source = new DOMParser().parseFromString(state.sections[section.section] || '', 'text/html')
            .querySelector(section.selector);
          if (!source || !element) throw new Error(window.cartStrings.error);
          return { target: element.querySelector(section.selector) || element, html: source.innerHTML };
        });
        replacements.forEach(({ target, html }) => { target.innerHTML = html; });
        document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer').forEach((element) => {
          element.classList.toggle('is-empty', state.item_count === 0);
        });

        const rows = Array.from(this.panel.querySelectorAll('.header-cart__item'));
        const updatedRow = rows.find((row) => row.dataset.itemKey === key) || rows[Math.min(index, rows.length - 1)];
        const list = this.panel.querySelector('.header-cart__items');
        if (list) list.scrollTop = listScroll;
        this.setBusy(false);
        if (!this.panel.hidden && (document.activeElement === document.body || document.activeElement === activeElement)) {
          const focusTarget = updatedRow?.querySelector(focusName === 'plus' || focusName === 'minus'
            ? `[name="${focusName}"]` : quantity === 0 ? '.header-cart__remove' : '.quantity__input');
          (focusTarget || this.panel.querySelector('.header-cart__button')).focus({ preventScroll: true });
        }

        const updatedItem = state.items.find((cartItem) => cartItem.key === key) || state.items[index];
        if (quantity > 0 && updatedItem && updatedItem.quantity !== quantity) {
          this.showError(window.cartStrings.quantityError.replace('[quantity]', updatedItem.quantity));
        }
        this.querySelector('[data-header-cart-status]').textContent =
          this.trigger.querySelector('.cart-count-bubble .visually-hidden')?.textContent ||
          this.panel.querySelector('.header-cart__empty')?.textContent || '';
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'header-cart', cartData: state });
      } catch (error) {
        if (error.name === 'AbortError') return;
        const input = item.querySelector('.quantity__input');
        input.value = input.defaultValue;
        input.closest('quantity-input').validateQtyRules();
        this.showError(failureMessage);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.updating = busy;
      this.panel.setAttribute('aria-busy', String(busy));
      this.panel.querySelectorAll('.header-cart__actions button, .header-cart__actions input').forEach((control) => {
        control.disabled = busy;
      });
      this.panel.querySelectorAll('.header-cart__remove').forEach((remove) => {
        if (busy) remove.setAttribute('aria-disabled', 'true');
        else remove.removeAttribute('aria-disabled');
      });
    }

    showError(message) {
      const error = this.panel.querySelector('.header-cart__error');
      error.textContent = message;
      error.hidden = !message;
    }

    setOpen(open) {
      this.panel.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      const stickyHeader = this.closest('sticky-header');
      if (stickyHeader) stickyHeader.preventHide = open;
    }
  });
}
